import { useCallback, useEffect, useMemo, useState } from "react";
import { applyRangeSelection } from "@src/common/lib/rangeSelection";

export interface SelectableStop {
  id: string;
  // Stops of the same shipment can only transfer together: one order can't
  // have different drivers for pickup and drop-off, so checking any stop of
  // an order pulls in its siblings.
  shipmentId?: string | null;
  // The day the stop is routed on. The route list only renders one day at a
  // time, so the date is what lets the selection span the whole route: stops
  // from days that are not currently shown stay selected instead of being
  // pruned as "no longer routed".
  stopDate?: string | null;
}

export interface StopSelection {
  selectedStopIds: string[];
  // Across every day of the route, not just the one shown.
  selectedCount: number;
  // Scoped to the stops currently rendered, so the header reads as
  // "x of the cards you are looking at".
  visibleSelectedCount: number;
  visibleTotalCount: number;
  // Selection mode is implicit: the list is in select mode as soon as one
  // card is checked, and leaves it when the last one is unchecked.
  isSelectionMode: boolean;
  isSelected: (stopId: string) => boolean;
  // True for a stop that is only selected because a sibling stop of its
  // order was checked; the card renders it in a subdued style so the
  // dispatcher sees what got pulled in.
  isAutoSelected: (stopId: string) => boolean;
  toggleStopAt: (index: number, extendRange?: boolean) => void;
}

/**
 * Checkbox selection over the stops rendered in the route list.
 *
 * `stops` must be in the same order as the rendered cards so that shift-click
 * range selection matches what the dispatcher sees. The rendered list may be a
 * single day's slice of a multi-day route: stops seen on previously shown days
 * are remembered, so a selection survives switching days and an order's
 * sibling stops are auto-checked even when they land on a different day.
 */
export function useStopSelection(stops: SelectableStop[]): StopSelection {
  // Every stop this hook has seen, across day slices, keyed by id. This is
  // the universe the selection lives in; `stops` is just the visible window.
  const [knownStopsById, setKnownStopsById] = useState<
    Map<string, SelectableStop>
  >(() => new Map(stops.map((stop) => [stop.id, stop])));
  const [manuallySelectedStopIds, setManuallySelectedStopIds] = useState<
    string[]
  >([]);
  const [lastToggledIndex, setLastToggledIndex] =
    useState<number | undefined>(undefined);

  // The route can change underneath the selection (stop date switch,
  // re-optimization, drag reorder). A stop that vanished from a day we are
  // looking at has left the route, so forget it — but a stop from a day that
  // is simply not shown right now stays known and stays selectable.
  useEffect(() => {
    setKnownStopsById((current) => {
      const next = new Map(current);
      const visibleIds = new Set(stops.map((stop) => stop.id));
      const visibleDates = new Set(stops.map((stop) => stop.stopDate ?? ""));
      let changed = false;
      next.forEach((stop, id) => {
        if (visibleDates.has(stop.stopDate ?? "") && !visibleIds.has(id)) {
          next.delete(id);
          changed = true;
        }
      });
      stops.forEach((stop) => {
        if (next.get(stop.id) !== stop) {
          next.set(stop.id, stop);
          changed = true;
        }
      });
      return changed ? next : current;
    });
    // Range anchors are indexes into the visible slice, so they are stale as
    // soon as the slice changes.
    setLastToggledIndex(undefined);
  }, [stops]);

  useEffect(() => {
    setManuallySelectedStopIds((current) => {
      const pruned = current.filter((id) => knownStopsById.has(id));
      return pruned.length === current.length ? current : pruned;
    });
  }, [knownStopsById]);

  const manualIdSet = useMemo(
    () => new Set(manuallySelectedStopIds),
    [manuallySelectedStopIds]
  );

  // Selected = the manually checked stops plus every known sibling stop of
  // their orders (dispatch is per order, not per stop).
  const selectedStopIds = useMemo(() => {
    const selectedShipmentIds = new Set<string>();
    manuallySelectedStopIds.forEach((id) => {
      const shipmentId = knownStopsById.get(id)?.shipmentId;
      if (shipmentId) selectedShipmentIds.add(shipmentId);
    });
    const siblingIds: string[] = [];
    knownStopsById.forEach((stop, id) => {
      if (
        !manualIdSet.has(id) &&
        stop.shipmentId &&
        selectedShipmentIds.has(stop.shipmentId)
      ) {
        siblingIds.push(id);
      }
    });
    return [...manuallySelectedStopIds, ...siblingIds];
  }, [knownStopsById, manualIdSet, manuallySelectedStopIds]);

  const selectedIdSet = useMemo(
    () => new Set(selectedStopIds),
    [selectedStopIds]
  );

  const isSelected = useCallback(
    (stopId: string) => selectedIdSet.has(stopId),
    [selectedIdSet]
  );

  const isAutoSelected = useCallback(
    (stopId: string) => selectedIdSet.has(stopId) && !manualIdSet.has(stopId),
    [manualIdSet, selectedIdSet]
  );

  const addRange = useCallback(
    (fromIndex: number, toIndex: number) => {
      const stopIds = stops.map((stop) => stop.id);
      setManuallySelectedStopIds((current) =>
        applyRangeSelection(current, stopIds, fromIndex, toIndex)
      );
    },
    [stops]
  );

  const toggleStopAt = useCallback(
    (index: number, extendRange = false) => {
      const stop = stops[index];
      if (!stop) return;
      if (extendRange && lastToggledIndex !== undefined) {
        addRange(lastToggledIndex, index);
        setLastToggledIndex(index);
        return;
      }
      if (selectedIdSet.has(stop.id)) {
        // Unchecking any stop of an order unchecks the whole order — its
        // stops can only move together, so a partial selection would lie.
        setManuallySelectedStopIds((current) =>
          current.filter((id) => {
            if (id === stop.id) return false;
            if (!stop.shipmentId) return true;
            return knownStopsById.get(id)?.shipmentId !== stop.shipmentId;
          })
        );
      } else {
        setManuallySelectedStopIds((current) => [...current, stop.id]);
      }
      setLastToggledIndex(index);
    },
    [addRange, knownStopsById, lastToggledIndex, selectedIdSet, stops]
  );

  const visibleSelectedCount = useMemo(
    () => stops.filter((stop) => selectedIdSet.has(stop.id)).length,
    [selectedIdSet, stops]
  );

  return {
    selectedStopIds,
    selectedCount: selectedStopIds.length,
    visibleSelectedCount,
    visibleTotalCount: stops.length,
    isSelectionMode: selectedStopIds.length > 0,
    isSelected,
    isAutoSelected,
    toggleStopAt,
  };
}
